import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Sparkles } from "lucide-react";
import { useGet } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { timeAgo } from "@/lib/fmt";
import { Loading } from "@/components/app/Bits";
import { Chips } from "@/components/app/RecordForm";
import { ReportCard } from "@/components/people/ReportCard";
import { ListenButton } from "@/components/app/ListenButton";

export function MyPerformance() {
  const [days, setDays] = useState(30);
  const { data, error } = useGet(`/me/performance?days=${days}`);
  if (error) return <div className="card-surface p-4 text-sm" data-testid="my-perf-error">{error.response?.data?.detail ? errMsg(error) : "Couldn't load your performance."}</div>;
  if (!data) return <Loading />;
  if (data.disabled) return <div className="card-surface p-5 text-sm text-stone-600" data-testid="my-perf-disabled">Your restaurant hasn't turned on the performance view yet.</div>;
  return (
    <div data-testid="my-performance" className="space-y-4">
      <h1 className="font-display text-3xl">My Growth</h1>
      <Chips options={["30 days", "90 days"]} value={days === 30 ? "30 days" : "90 days"} onChange={(v) => v && setDays(v === "30 days" ? 30 : 90)} testid="my-perf-range" />
      <ReportCard d={data} employeeView />
      <div className="space-y-2" data-testid="my-feedback"><div className="font-medium">Coaching & recognition</div>
        {data.feedback.length ? data.feedback.map((n) => <div key={n.id} className="card-surface p-3 text-sm"><div className="text-xs text-stone-500 capitalize">{`${n.type} · ${n.author_name} · ${timeAgo(n.created_at)}`}</div><div className="whitespace-pre-wrap">{n.body}</div></div>)
          : <div className="text-sm text-stone-500">No feedback yet.</div>}</div>
    </div>
  );
}

const STARTERS = ["How can I increase my check average?", "What dishes should I recommend together?", "How can I improve wine sales?", "Which categories am I strongest in?", "How can I increase my tips?"];

export function AskCoach() {
  const [sid, setSid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (text) => {
    const m = (text ?? q).trim();
    if (!m || busy) return;
    setMsgs((x) => [...x, { role: "employee", text: m }]); setQ(""); setBusy(true);
    try { const { data } = await api.post("/me/ask", { message: m, session_id: sid }); setSid(data.session_id); setMsgs((x) => [...x, { role: "assistant", text: data.answer }]); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="ask-coach" className="space-y-3">
      <h1 className="font-display text-3xl flex items-center gap-2"><Sparkles className="h-6 w-6" />Ask 86'd</h1>
      <p className="text-sm text-stone-600">Menu pairings, selling ideas and coaching based on your own numbers. Business financials stay with management.</p>
      {!msgs.length && <div className="flex flex-wrap gap-2">{STARTERS.map((s, i) => <button key={s} onClick={() => send(s)} className="text-sm rounded-full border bg-white px-3 py-2 text-left" data-testid={`ask-starter-${i}`}>{s}</button>)}</div>}
      <div className="space-y-2" data-testid="ask-messages">{msgs.map((m, i) => <div key={i} className={`rounded-lg p-3 text-sm whitespace-pre-wrap ${m.role === "employee" ? "bg-stone-900 text-white ml-8" : "card-surface mr-4"}`} data-testid={`ask-msg-${i}`}>{m.text}{m.role !== "employee" && <div className="mt-2"><ListenButton text={m.text} testid={`ask-msg-listen-${i}`} /></div>}</div>)}
        {busy && <div className="text-sm text-stone-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Thinking…</div>}</div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 sticky bottom-20">
        <input className="flex-1 h-12 rounded-full border bg-white px-4" placeholder="Ask about the menu, selling, your goals…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="ask-input" />
        <button className="h-12 w-12 rounded-full bg-stone-900 text-white flex items-center justify-center" aria-label="Send" data-testid="ask-send"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
}
